import { useState } from 'react';
import Button from './ui/Button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from './ui/Card';
import Shell from './ui/Shell';

interface ConsentScreenProps {
  candidateName?: string | null;
  numQuestions: number;
  /** Optional override from the backend (Markdown). If absent, we show the default copy. */
  consentTextMd?: string;
  onAgree: () => void | Promise<void>;
}

/**
 * First screen: plain-English privacy notice + required checkbox.
 * We intentionally render the default copy inline (rather than parsing
 * the backend's Markdown) so the contract wording is always in the bundle
 * and visible to reviewers. The backend copy is shown as a secondary note.
 */
export default function ConsentScreen({
  candidateName,
  numQuestions,
  consentTextMd,
  onAgree,
}: ConsentScreenProps) {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleStart() {
    if (!agreed || submitting) return;
    setErr(null);
    setSubmitting(true);
    try {
      await onAgree();
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Could not record your consent.';
      setErr(message);
      setSubmitting(false);
    }
  }

  return (
    <Shell maxWidth="default">
      <Card>
        <CardHeader>
          <CardTitle>
            {candidateName ? `Welcome, ${candidateName}.` : 'Welcome.'}
          </CardTitle>
          <p className="text-base text-foreground/80">
            This is a short, automated voice interview. You will hear and answer{' '}
            <strong className="font-semibold text-foreground">{numQuestions}</strong>{' '}
            {numQuestions === 1 ? 'question' : 'questions'} in English. You can
            take your time — there is no ticking clock on individual answers.
          </p>
        </CardHeader>

        <CardContent className="space-y-5">
          <section className="space-y-4 text-sm leading-relaxed">
            <div>
              <h2 className="mb-1.5 text-sm font-semibold text-foreground">
                What happens during this interview
              </h2>
              <ul className="list-disc space-y-1 pl-5 text-foreground/80">
                <li>
                  An AI interviewer will read each question out loud. You can replay
                  it if needed.
                </li>
                <li>
                  To answer, you <strong className="font-semibold text-foreground">press and hold</strong> a microphone button
                  (or hold the space bar), speak, and release.
                </li>
                <li>
                  A human recruiter reviews your answers and transcripts afterwards.
                  No hiring decision is made by AI alone.
                </li>
              </ul>
            </div>

            <div>
              <h2 className="mb-1.5 text-sm font-semibold text-foreground">What we record</h2>
              <ul className="list-disc space-y-1 pl-5 text-foreground/80">
                <li>
                  The <strong className="font-semibold text-foreground">audio of your answers</strong> only — not your screen,
                  not your video, and nothing between questions.
                </li>
                <li>
                  An <strong className="font-semibold text-foreground">automatic transcript</strong> of what you said, produced
                  by a speech-to-text model.
                </li>
              </ul>
            </div>

            <div>
              <h2 className="mb-1.5 text-sm font-semibold text-foreground">Where it goes</h2>
              <ul className="list-disc space-y-1 pl-5 text-foreground/80">
                <li>
                  Your recordings and transcripts are sent to the recruiter who
                  shared this link with you.
                </li>
                <li>
                  They are kept only for the duration of the current recruiting
                  process and then deleted.
                </li>
                <li>
                  The data is not used to train AI models and is not shared with
                  third parties beyond the tools needed to deliver the interview.
                </li>
              </ul>
            </div>

            <div>
              <h2 className="mb-1.5 text-sm font-semibold text-foreground">Your choice</h2>
              <ul className="list-disc space-y-1 pl-5 text-foreground/80">
                <li>Participation is voluntary.</li>
                <li>
                  You can stop at any time by closing this tab — answers you have
                  already submitted remain with the recruiter, but no new ones will
                  be collected.
                </li>
              </ul>
            </div>

            {consentTextMd && (
              <details className="rounded-md border border-border bg-muted/40 px-3 py-2">
                <summary className="cursor-pointer text-sm text-muted-foreground">
                  Additional notice from the recruiter
                </summary>
                <pre className="mt-2 whitespace-pre-wrap rounded-md border border-border bg-background p-3 font-mono text-xs leading-relaxed text-foreground/80">
                  {consentTextMd}
                </pre>
              </details>
            )}
          </section>

          <label className="flex cursor-pointer items-start gap-3 rounded-md border border-border bg-muted/40 p-3 text-sm">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-primary"
            />
            <span>I have read and agree to the above.</span>
          </label>

          {err && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {err}
            </p>
          )}
        </CardContent>

        <CardFooter>
          <Button
            size="lg"
            disabled={!agreed || submitting}
            onClick={handleStart}
          >
            {submitting ? 'Starting…' : 'I agree — start interview'}
          </Button>
        </CardFooter>
      </Card>
    </Shell>
  );
}
